import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  CONTACT_EMAIL, CONTACT_NAME, DURATION_OF_INVESTMENT, EQUITY, INVESTOR,
  SHARE_CATEGORY,
} from 'constants/formFields';
import { OPTION_CATEGORY_INVESTOR } from 'constants/general';
import { BUTTON_VARIANT, NUMBER_FORMATS } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHInvestorsMutation, useUpdateMaterialitySHInvestorsMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateInvestorForm from './hooks/useCreateInvestorForm';

import type IMAInvestorsItem from 'store/slices/materialAssessment/interfaces/IMAInvestorItem';
import type IFieldError from 'store/types/IFieldError';
import type { TInvestorCreateSchema } from 'utils/validators';
import type ICreateInvestor from './interfaces/ICreateInvestor';

import styles from './scss/CreateInvestor.module.scss';

function CreateInvestor({
  open, onClose, title, selectedItem, setOpenModal, recordId, currentId, setInvestors,
  vcmId,
}: ICreateInvestor) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createInvestor] = useCreateMaterialitySHInvestorsMutation();
  const [updateInvestor] = useUpdateMaterialitySHInvestorsMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateInvestorForm({ isPageLoading: isLoading, selectedItem });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddInvestor = (investor: IMAInvestorsItem) => {
    if (currentId !== -1) {
      updateInvestor({
        id: currentId,
        maId: recordId,
        vcmId,
        data: investor,
      }).then((request) => {
        toast.success(i18n.t('notifications.updatedInvestor'));
        if ('data' in request && request.data?.data) {
          setInvestors((prev) => {
            const index = prev.findIndex((item) => item.id === currentId);
            if (index !== -1) {
              const updatedInvestors = [...prev];
              updatedInvestors[index] = { ...updatedInvestors[index], ...request.data.data };
              return updatedInvestors;
            }
            return prev;
          });
          setOpenModal(false);
        }
      }).catch((errors) => {
        setFieldsErrors<TInvestorCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createInvestor({
        data: investor,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdInvestor'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TInvestorCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddInvestor({
      name: data[INVESTOR],
      contactName: data[CONTACT_NAME],
      contactEmail: data[CONTACT_EMAIL] || '',
      equityPercentage: Number(data[EQUITY]) || 0,
      investmentDuration: data[DURATION_OF_INVESTMENT] || '',
      shareCategory: data[SHARE_CATEGORY]?.value || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={INVESTOR}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.investor')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.contactName')}
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactEmail')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={EQUITY}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <NumberField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.equity')}
                error={error?.message}
                onChange={(values) => onChange(values.value)}
                type={NUMBER_FORMATS.percent}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                allowNegative={false}
                required
              />
            )}
          />
          <Controller
            control={control}
            name={SHARE_CATEGORY}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.shareCategory')}
                placeholder={t('materialityAssessment.placeholder.selectCategory')}
                options={OPTION_CATEGORY_INVESTOR}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={DURATION_OF_INVESTMENT}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <NumberField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.durationOfInvestment')}
                error={error?.message}
                onChange={(values) => onChange(values.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                allowNegative={false}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateInvestor;
